import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { coursesAPI } from '../services/api';
import { APP_URL } from '../config';

export const useCourseDataForSEO = () => {
  const location = useLocation();
  
  // Извлекаем courseSlug из pathname
  const getCourseSlugFromPath = (pathname) => {
    const match = pathname.match(/^\/courses\/([^/]+)$/);
    return match ? match[1] : null;
  };
  
  const courseSlug = getCourseSlugFromPath(location.pathname);
  const [courseData, setCourseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (courseSlug) {
      setLoading(true);
      setError(null);
      
      coursesAPI.getBySlugWithProfessions(courseSlug)
        .then(response => {
          if (response.data) {
            const course = response.data;
            
            // Формируем полные URL для изображений
            const getFullImageUrl = (imagePath) => {
              if (!imagePath) return null;
              return imagePath.startsWith('http') ? imagePath : `${APP_URL}${imagePath}`;
            };

            // Формируем структурированные данные для SEO
            setCourseData({
              // Основные данные курса
              title: course.title,
              description: course.description,
              difficulty: course.difficulty,
              duration: course.duration,
              rating: course.rating,
              
              // Статистика
              totalLessons: course.lessonCount || 0,
              totalTests: course.testCount || 0,
              
              // Изображения
              image: getFullImageUrl(course.image),
              thumbnail: getFullImageUrl(course.thumbnail),
              
              // Дополнительные данные для SEO
              professions: course.professions || [],
              tags: course.tags || [],
              
              // URL для Open Graph
              url: `${APP_URL}/courses/${courseSlug}`,
              
              // Мета-данные
              keywords: [
                course.title,
                course.difficulty,
                ...(course.professions || []).map(p => p.name),
                ...(course.tags || [])
              ].filter(Boolean).join(', '),
              
              // Structured Data
              structuredData: {
                "@context": "https://schema.org",
                "@type": "Course",
                "name": course.title,
                "description": course.description,
                "provider": {
                  "@type": "Organization",
                  "name": "Learning Platform"
                },
                "offers": {
                  "@type": "Offer",
                  "price": "0",
                  "priceCurrency": "USD"
                },
                "courseMode": "online",
                "educationalLevel": course.difficulty,
                "timeRequired": `PT${course.duration || 0}M`,
                "image": getFullImageUrl(course.image),
                "aggregateRating": course.rating ? {
                  "@type": "AggregateRating",
                  "ratingValue": course.rating,
                  "ratingCount": course.ratingCount || 1
                } : undefined
              }
            });
          } else {
            setError('Course not found');
          }
        })
        .catch(error => {
          console.error('Error fetching course data for SEO:', error);
          setError(error.message || 'Failed to fetch course data');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [courseSlug, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return { courseData, loading, error };
};

export default useCourseDataForSEO;
